import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';
import RatingChip from '../shared/RatingChip';
import PricePill from '../shared/PricePill';
import PopularTag from '../shared/PopularTag';
import { calculateOriginalPrice } from '../../utils/priceComparison';
import ProductOptionsSheet from './ProductOptionsSheet';

export default function ProductDetailSheet({
  product,
  isOpen,
  onClose,
  onAddToCart,
  isBusinessOpen = true,
  // Texto del botón cuando no se puede agregar: "Cerrado" (negocio cerrado)
  // o "No disponible" (el producto).
  disabledLabel = 'Cerrado',
}) {
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isOptionsSheetOpen, setIsOptionsSheetOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsOptionsSheetOpen(false);
    }
  }, [isOpen]);

  const displayRating = product?.rating || 4.5;
  const hasPopularTag = product?.isPopular === true;

  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const startYRef = useRef(0);

  // Bloquear scroll del fondo cuando el modal está abierto
  useEffect(() => {
    if (!modalVisible) return;

    const scrollY = window.scrollY;

    const preventScroll = (e) => {
      const modal = e.target.closest?.('[data-product-modal]');
      if (modal) return;
      e.preventDefault();
    };

    document.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('wheel', preventScroll, { passive: false });

    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;

    return () => {
      document.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('wheel', preventScroll);
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, [modalVisible]);

  // ✅ Normalizar URL de imagen
  const productImage = useMemo(() => {
    if (!product?.image) return null;
    
    if (product.image.startsWith('http')) {
      return product.image;
    }
    
    if (product.image.includes('cloudfront.net') || product.image.includes('amazonaws.com')) {
      return `https://${product.image}`;
    }
    
    return product.image;
  }, [product?.image]);

  const handleAdd = () => {
    const hasExtras = product?.optionGroups && product.optionGroups.length > 0;

    if (hasExtras) {
      setIsOptionsSheetOpen(true);
      return;
    }

    const productToAdd = selectedSize
      ? { ...product, selectedSizeLabel: selectedSize.label, price: selectedSize.price }
      : product;

    onAddToCart(productToAdd, quantity);
    handleClose();
  };

  useEffect(() => {
    if (isOpen && product) {
      setQuantity(1);
      setSelectedSize(product?.sizes?.[0] || null);
      setImageLoaded(false);
      setImageError(false);
      setDragY(0);
      setIsDragging(false);

      // Animación de entrada
      setTimeout(() => setModalVisible(true), 10);
    } else {
      setModalVisible(false);
    }
  }, [isOpen, product]);


  if (!product || !isOpen) return null;

  const handleClose = () => {
    setModalVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // ✅ Touch handlers mejorados
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    startYRef.current = touch.clientY;
    
    // Verificar si el contenido está en la parte superior
    const content = contentRef.current;
    const isAtTop = content ? content.scrollTop === 0 : true;
    
    if (isAtTop) {
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - startYRef.current;
    
    // Solo permitir arrastrar hacia abajo y prevenir pull-to-refresh
    if (deltaY > 0) {
      e.preventDefault();
      setDragY(deltaY);
    } else {
      setIsDragging(false);
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Si arrastra más de 100px, cerrar
    if (dragY > 100) {
      handleClose();
    } else {
      // Volver a la posición original
      setDragY(0);
    }
  };

  // ✅ Click fuera del modal para cerrar
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (typeof document === 'undefined') return null;

  // Portal a body para escapar transform ancestors (slider de MarketLayout).
  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex flex-col items-center justify-end transition-all duration-300 ${
        modalVisible ? 'bg-black/50' : 'bg-transparent'
      }`}
      onClick={handleOverlayClick}
      style={{ touchAction: isDragging ? 'none' : 'auto' }}
    >
      {/* Botón X flotante — arriba del sheet */}
      {modalVisible && (
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          className="mb-3 w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center"
        >
          <X size={22} weight="bold" className="text-white" />
        </button>
      )}

      <div
        ref={modalRef}
        data-product-modal
        className="relative bg-white rounded-t-[2rem] w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden shadow-xl transition-transform duration-300 ease-out"
        style={{
          transform: isDragging 
            ? `translateY(${dragY}px)` 
            : modalVisible 
              ? 'translateY(0)' 
              : 'translateY(100%)',
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Imagen - Ahora ocupa todo el espacio */}
        <div
          className="relative w-full h-64 overflow-hidden bg-white"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Tag Popular sobre la imagen */}
          {hasPopularTag && (
            <PopularTag size="lg" className="absolute top-3 left-3 z-10" />
          )}

          {/* Skeleton Loader */}
          {!imageLoaded && !imageError && productImage && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 bg-gray-200 rounded-lg animate-pulse"></div>
            </div>
          )}

          {/* Imagen del Producto - Cover para llenar el espacio */}
          {productImage && !imageError ? (
            <img
              src={productImage}
              alt={product.name}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="eager"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                console.error('Error cargando imagen:', productImage);
                setImageError(true);
              }}
            />
          ) : null}

          {/* Placeholder si no hay imagen */}
          {(imageError || !productImage) && (
            <div className="absolute inset-0 flex items-center justify-center ">
              <span className="text-6xl">🍽️</span>
            </div>
          )}

        </div>

        {/* Contenido */}
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto px-5 pt-5 pb-6"
          style={{
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* Row info + botón Agregar */}
          <div className="flex items-start gap-3 mb-5">

            {/* Columna izquierda: nombre + precio + rating */}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold font-display tracking-tight text-gray-900 leading-tight mb-1.5">
                {product.name}
              </h2>

              <div className="flex items-center gap-2 mb-5">
                {(() => {
                  const orig = calculateOriginalPrice(product.price, product._id || product.id);
                  if (!orig) return null;
                  return (
                    <span className="text-xs text-gray-400 line-through">
                      ${orig}
                    </span>
                  );
                })()}
                <PricePill price={product.price} />
              </div>

              {displayRating && (
                <RatingChip rating={displayRating} size="md" />
              )}
            </div>

            {/* Columna derecha: botón Agregar + Customizable */}
            <div className="flex-shrink-0 flex flex-col items-center gap-1">
              <button
                onClick={handleAdd}
                disabled={!isBusinessOpen}
                className={`relative rounded-full p-[2px] active:scale-95 transition-transform ${
                  isBusinessOpen ? '' : 'opacity-50 cursor-not-allowed'
                }`}
                style={{
                  background: isBusinessOpen
                    ? 'linear-gradient(to top, var(--color-product-btn-ring-from), var(--color-product-btn-ring-to))'
                    : '#D1D5DB'
                }}
              >
                <div
                  className="rounded-full px-8 py-2.5 text-base font-bold"
                  style={{
                    background: isBusinessOpen
                      ? 'linear-gradient(to top, var(--color-secondary-100) 0%, #ffffff 100%)'
                      : '#F9FAFB',
                    color: isBusinessOpen ? 'var(--color-product-btn-text)' : '#9CA3AF'
                  }}
                >
                  {isBusinessOpen ? 'Agregar' : disabledLabel}
                </div>
              </button>
              {product.optionGroups && product.optionGroups.length > 0 && (
                <span className="text-xs text-gray-500 font-medium">Personalizable</span>
              )}
            </div>
          </div>

          {/* Descripción */}
          {product.description && (
            <p className="text-[15px] text-gray-600 leading-relaxed mb-5">
              {product.description}
            </p>
          )}

          {/* Selector de tamaño */}
          {product.sizes && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-bold text-gray-900">
                  Elige tu tamaño
                </h3>
                <span className="text-xs text-gray-700 bg-transparent border border-gray-300 rounded-full px-2 py-0.5">
                  Obligatorio
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Seleccione mínimo 1 opción
              </p>

              <div className="divide-y divide-gray-200">
                {product.sizes.map((size, index) => {
                  const selected = selectedSize?.label === size.label;
                  return (
                    <div
                      key={index}
                      onClick={() => setSelectedSize(size)}
                      className="flex items-center justify-between py-3 cursor-pointer"
                    >
                      <div>
                        <h4 className="text-sm text-gray-700">{size.label}</h4>
                        <p className="text-base font-semibold text-gray-900">
                          MXN {size.price.toFixed(2)}
                        </p>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-full border-2 ${
                          selected
                            ? 'border-brand-primary bg-brand-primary'
                            : 'border-gray-300 bg-white'
                        } flex items-center justify-center`}
                      >
                        {selected && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

      </div>

      <ProductOptionsSheet
        isOpen={isOptionsSheetOpen}
        onClose={() => setIsOptionsSheetOpen(false)}
        product={product}
        onConfirm={(prod, qty, extras) => {
          onAddToCart(prod, qty, extras);
          setIsOptionsSheetOpen(false);
          handleClose();
        }}
      />
    </div>,
    document.body
  );
}